// oxlint-disable-next-line no-restricted-imports
import { matchPath } from 'react-router';
import { fromJS, List } from '@gonfalon/immutable-shim/immutableShim';
import type { FormState } from '@gonfalon/legacy-forms/formUtils';
import { createFormState } from '@gonfalon/legacy-forms/formUtils';
import { createSelector } from 'reselect';

import type { BillingAction } from '#actions/billing';
import type { FormAction } from '#actions/forms';
import type { MemberAction } from '#actions/members';
import { createRequestReducer } from '#reducers/createRequestReducer';
import { reducerRegistry as registry } from '#reducers/registry';
import type { GlobalState } from '#reducers/state';
import type { BulkMemberInvite } from '#utils/accounts/records/BulkInvites';
import { createBulkMemberInvite, createSuggestInviteMembers } from '#utils/accounts/records/BulkInvites';
import type { NewJoinTeamRequest } from '#utils/accounts/records/Invitations';
import { createNewJoinTeamRequest } from '#utils/accounts/records/Invitations';

// oxlint-disable-next-line import/no-unassigned-import
import '#epics/members';

function getInitialBulkInviteState(initialState = {}) {
  const state = createFormState(createBulkMemberInvite(initialState));
  const match = matchPath('/settings/members/invite/:inviteEmail', window.location.pathname);
  if (match?.params.inviteEmail) {
    // allows for multiple emails in the URL separated by a comma such as:
    // /settings/members/invite/email1@launchdarkly.com%2Cemail2@launchdarkly.com
    const initialData = createBulkMemberInvite({ emails: decodeURIComponent(match.params.inviteEmail).split(',') });
    return state.trackField('emails').revalidate(initialData);
  }

  return state;
}

export function bulkInviteForm(state = getInitialBulkInviteState(), action: MemberAction | FormAction | BillingAction) {
  switch (action.type) {
    case 'forms/INITIALIZE':
      if (action.model !== 'bulkInviteForm') {
        return state;
      }
      return getInitialBulkInviteState(action.initialState as FormState<BulkMemberInvite>);
    case 'members/EDIT_BULK_INVITE':
      const { field, value, seatsLeft } = action;
      const modified = state.modified.set(field, value);
      return state.trackField(field).revalidate(modified, seatsLeft);
    case 'members/INVITE_MEMBERS':
    case 'billing/SUBSCRIBE':
      return state.submitting();
    case 'members/INVITE_MEMBERS_DONE':
    case 'billing/SUBSCRIBE_AND_INVITE_DONE':
      const failedEmails = action.invites.failedEmails;
      const subscriptionSuccessful = action.options?.inviteAndSubscribe;
      const rateLimitedEmails = failedEmails.get('rate_limited', List());
      const rest = failedEmails.delete('rate_limited').valueSeq().flatten(true).toList();
      if (rest.size) {
        return state.submitFailed(
          action.invites,
          fromJS({
            rateLimitedEmails,
            failedEmails: rest,
            successfulCount: action.invites.successfulEmails.size,
            subscriptionSuccessful,
          }),
        );
      } else {
        return getInitialBulkInviteState();
      }
    case 'members/BULK_INVITE_MEMBERS_FAILED':
      let errorResponse = action.error;
      if (!action?.error?.get('status')) {
        // If an unknown error occurs, set the status so we can show the error message and close the modal.
        errorResponse = action.error.set('status', 500);
      }
      return state.submitFailed(action.invites, errorResponse);
    case 'forms/BLUR':
      if (action.model !== 'bulkInviteForm') {
        return state;
      }
      return state.handleBlur(action.field, state.modified);
    case 'forms/DESTROY':
      if (action.model !== 'bulkInviteForm') {
        return state;
      }
      return getInitialBulkInviteState();
    default:
      return state;
  }
}

export const bulkInviteProgress = (
  state = fromJS({
    total: undefined,
    completed: 0,
  }),
  action: MemberAction | BillingAction,
) => {
  switch (action.type) {
    case 'members/INVITE_MEMBERS':
      return state.set('completed', 0).set('total', action.invites.size());
    case 'members/INVITE_MEMBERS_BATCH_DONE':
      return state.update('completed', (p: number) => p + action.members.size);
    case 'members/INVITE_MEMBERS_DONE':
    case 'billing/SUBSCRIBE_AND_INVITE_DONE':
      return state.set('completed', state.get('total'));
    default:
      return state;
  }
};

const initialSuggestInviteMembersState = createFormState(createSuggestInviteMembers({ emails: [] }));
export function suggestInviteMembersForm(state = initialSuggestInviteMembersState, action: MemberAction | FormAction) {
  switch (action.type) {
    case 'members/EDIT_SUGGEST_INVITE_MEMBERS':
      const { field, value } = action;
      const modified = state.modified.set(field, value);
      return state.trackField(field).revalidate(modified);
    case 'forms/BLUR':
      if (action.model !== 'suggestInviteMembersForm') {
        return state;
      }
      return state.handleBlur(action.field, state.modified);
    case 'forms/DESTROY':
      if (action.model !== 'suggestInviteMembersForm') {
        return state;
      }
      return initialSuggestInviteMembersState;
    case 'members/SEND_SUGGEST_INVITE_MEMBERS':
      return state.submitting();
    case 'members/SEND_SUGGEST_INVITE_MEMBERS_DONE':
      return initialSuggestInviteMembersState;
    case 'members/SEND_SUGGEST_INVITE_MEMBERS_FAILED':
      let errorResponse = action.error;
      if (!action?.error?.get('status')) {
        errorResponse = action.error.set('status', 500);
      }
      return state.submitFailed(action.suggestedInvites, errorResponse);
    default:
      return state;
  }
}

const initialInviteRequestState = fromJS({
  form: createFormState(createNewJoinTeamRequest()),
});

const initialBulkInvitedMembersState = fromJS({
  members: [],
  failedMembers: [],
});

export const bulkInvitedMembers = (state = initialBulkInvitedMembersState, action: MemberAction) => {
  switch (action.type) {
    case 'members/INVITE_MEMBERS_BATCH_DONE':
      return state.merge({
        members: action.members,
        failedMembers: action.failedMembers,
      });
    default:
      return state;
  }
};

export function requestTeamInviteForm(state = initialInviteRequestState, action: FormAction | MemberAction) {
  switch (action.type) {
    case 'members/EDIT_NEW_TEAM_INVITE_REQUEST':
      return state.update('form', (f: FormState<NewJoinTeamRequest>) =>
        f.trackField(action.field).revalidate(action.account),
      );
    case 'members/REQUEST_TEAM_INVITE':
      return state.update('form', (f: FormState<NewJoinTeamRequest>) => f.submitting());
    case 'members/REQUEST_TEAM_INVITE_DONE':
      return state.update('form', (f: FormState<NewJoinTeamRequest>) => f.submitted());
    case 'members/REQUEST_TEAM_INVITE_FAILED':
      return state.update('form', (f: FormState<NewJoinTeamRequest>) =>
        f.submitFailed(action.teamInviteRequest, action.error),
      );
    case 'forms/BLUR':
      if (action.model !== 'requestTeamInviteForm') {
        return state;
      }
      return state.update('form', (f: FormState<NewJoinTeamRequest>) => f.handleBlur(action.field, f.modified));
    default:
      return state;
  }
}
export const bulkInviteFormSelector = (state: GlobalState) => state.bulkInviteForm;
export const requestTeamInviteFormSelector = (state: GlobalState) => state.requestTeamInviteForm.get('form');

export const bulkInvitedMembersSelector = (state: GlobalState) => state.bulkInvitedMembers;

export const suggestInviteMembersFormSelector = (state: GlobalState) => state.suggestInviteMembersForm;

export const bulkInviteProgressSelector = createSelector(
  (state: GlobalState) => state.bulkInviteProgress,
  (bip) => {
    const total = bip.get('total');
    const completed = bip.get('completed');

    return {
      total,
      completed,
      percentage: total === undefined || total === null || total === 0 ? 0 : completed / total,
    };
  },
);

export const approveUnverifiedMemberRequest = createRequestReducer([
  'members/APPROVE_UNVERIFIED_MEMBER_REQUEST_START',
  'members/APPROVE_UNVERIFIED_MEMBER_REQUEST_DONE',
  'members/APPROVE_UNVERIFIED_MEMBER_REQUEST_FAILED',
]);

registry.addReducers({
  bulkInviteForm,
  bulkInviteProgress,
  requestTeamInviteForm,
  bulkInvitedMembers,
  approveUnverifiedMemberRequest,
  suggestInviteMembersForm,
});

declare module '#reducers/state' {
  interface GlobalState {
    bulkInviteForm: ReturnType<typeof bulkInviteForm>;
    requestTeamInviteForm: ReturnType<typeof requestTeamInviteForm>;
    bulkInvitedMembers: ReturnType<typeof bulkInvitedMembers>;
    approveUnverifiedMemberRequest: ReturnType<typeof approveUnverifiedMemberRequest>;
    suggestInviteMembersForm: ReturnType<typeof suggestInviteMembersForm>;
    bulkInviteProgress: ReturnType<typeof bulkInviteProgress>;
  }
}
